import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, catchError, endWith, forkJoin, map, of, repeat, startWith, switchMap, takeWhile, tap } from 'rxjs';
import { PropertyConfig } from 'src/app/shared/config/property.config';
import { SelectedSourceDataPipe } from 'src/app/shared/pipes/selected-source-data.pipe';
import { ProxyService } from 'src/app/shared/services/proxy.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { CommonService } from 'src/app/shared/utility/common.service';
import { DocumentPreviewApi } from '../../document-preview/api/document-preview.api';
import { LongTermRentalState } from '../../long-term-rental/state/long-term-rental.state';
import { PropertyMap } from '../../order/interface/order.interface';
import { ComparableService } from '../../order/service/comparable.service';
import { ApproachFactoryState } from '../../order/state/approach-factory.state';
import { CostApproachState } from '../../order/state/cost-approach.state';
import { OrderState } from '../../order/state/order.state';
import { SalesApproachState } from '../../order/state/sales-approach.state';
import { ScenarioState } from '../../scenario/state/scenario.state';
import { ShortTermRentalState } from '../../short-term-rental/state/short-term-rental.state';
import { SyncState } from '../../sync/state/sync.state';
import { PropertyApi } from '../api/property.api';
import {
  RemoveUnitsDialogComponent,
  RemoveUnitsDialogData,
} from '../component/remove-units-dialog/remove-units-dialog.component';
import {
  UnitCountDialogComponent,
  UnitCountDialogData,
} from '../component/unit-count-dialog/unit-count-dialog.component';
import {
  ExtendedPropertyApiDetail,
  ExtendedPropertyDetail,
  GenerateListingSheetApiRequest,
  PropertyAttribute,
  PropertyAttributeValue,
  PropertyDataApiRequest,
  PropertyDetail,
  PropertySummary,
  PropertyViewApiRequest,
  StandardStatusType,
  SubjectPropertyFilter,
  UnitDetail,
  UnitOpScope,
} from '../interface/property.interface';
import { PropertyState } from '../state/property.state';
import { PropertySummaryService } from './property-summary.service';

@Injectable({
  providedIn: 'root',
})
export class PropertyService {
  /** Last accepted PropertySubType per property id, kept to detect and revert changes. */
  private _knownPropertySubType = new Map<string, string | null | undefined>();
  private _knownPropertySubTypeAttr = new Map<string, PropertyAttributeValue | null>();

  /** Property ids with an in-flight unit operation, and the value each is expected to settle at. */
  private _propertySubTypeInFlight = new Set<string>();
  private _propertySubTypeSettleTarget = new Map<string, string | null | undefined>();

  /** Comparables claimed by a copy of PropertySubType that is under way in the grid. */
  private _propertySubTypeCopyClaim = new Set<string>();

  constructor(
    private approachFactoryState: ApproachFactoryState,
    private commonService: CommonService,
    private comparableService: ComparableService,
    private costApproachState: CostApproachState,
    private dialog: MatDialog,
    private documentPreviewApi: DocumentPreviewApi,
    private longTermRentalState: LongTermRentalState,
    private orderState: OrderState,
    private propertyApi: PropertyApi,
    private scenarioState: ScenarioState,
    private propertyState: PropertyState,
    private propertySummaryService: PropertySummaryService,
    private proxyService: ProxyService,
    private salesApproachState: SalesApproachState,
    private selectedSourceData: SelectedSourceDataPipe,
    private selectedSourceDataPipe: SelectedSourceDataPipe,
    private shortTermRentalState: ShortTermRentalState,
    private snackBarService: SnackBarService,
    private syncState: SyncState
  ) {
    this._listenToPropertyDataChanges();
    this._watchPropertySubTypeChanges();
  }

  /** Fetches the property view layout once per order; result stored in propertyState. */
  fetchPropertyViewLayout(reportType: string): Observable<boolean> {
    const requestBody: PropertyViewApiRequest = {
      pageName: 'propertyDetails',
      reportType,
    };
    return this.propertyApi.getActivePropertyPageView(requestBody).pipe(
      map((res) => {
        this.propertyState.propertyPageLayout = res;
        return false;
      }),
      catchError(() => {
        this.propertyState.propertyPageLayout = null;
        return of(false);
      })
    );
  }

  fetchSubjectViewLayout(reportType: string): Observable<boolean> {
    const requestBody: PropertyViewApiRequest = {
      pageName: 'subjectDetails',
      reportType,
    };
    return this.propertyApi.getActivePropertyPageView(requestBody).pipe(
      map((res) => {
        this.propertyState.subjectPageLayout = res;
        return false;
      }),
      catchError(() => {
        this.propertyState.subjectPageLayout = null;
        return of(false);
      })
    );
  }

  fetchComparableViewLayout(reportType: string): Observable<boolean> {
    const requestBody: PropertyViewApiRequest = {
      pageName: 'comparableDetails',
      reportType,
    };

    return this.propertyApi.getActivePropertyPageView(requestBody).pipe(
      map((res) => {
        this.propertyState.comparablePageLayout = res;
        return false;
      }),
      catchError(() => {
        this.propertyState.comparablePageLayout = structuredClone(this.propertyState.propertyPageLayout);
        return of(false);
      })
    );
  }

  /** Fetches the project information page view layout. */
  fetchProjectViewLayout(reportType: string): Observable<boolean> {
    const requestBody: PropertyViewApiRequest = {
      pageName: 'projectInformation',
      reportType,
    };
    return this.propertyApi.getActivePropertyPageView(requestBody).pipe(
      map((res) => {
        this.propertyState.projectPageLayout = res;
        return false;
      }),
      catchError(() => {
        this.propertyState.projectPageLayout = null;
        return of(false);
      })
    );
  }

  /** Fetches the subject / comparable page images view layout. */
  fetchImageViewLayout(reportType: string, isSubject: boolean): Observable<boolean> {
    const requestBody: PropertyViewApiRequest = {
      pageName: isSubject ? 'subjectPropertyImage' : 'comparablePropertyImage',
      reportType,
    };
    const layoutKey = isSubject ? 'subjectImageLayout' : 'comparableImageLayout';

    if (this.propertyState[layoutKey]?.sectionList?.length) return of(false);

    return this.propertyApi.getActivePropertyPageView(requestBody).pipe(
      map((res) => {
        this.propertyState[layoutKey] = res;

        return false;
      }),
      catchError(() => {
        this.propertyState[layoutKey] = null;
        return of(false);
      })
    );
  }

  /** Fetches the subject property's details once per order. */
  fetchSubjectPropertyDetail(reportType: string, propertyId?: string, listingId?: string): Observable<boolean> {
    return this._fetchPropertyData(reportType, propertyId ?? '', listingId).pipe(
      map((data) => {
        // this.propertyState.subjectPropertyView = view.sectionList;
        this.propertyState.subjectPropertyData = data;
        this.syncState.syncedSubjectPropertyData = data;
        if (data.id == this.propertyState.activePropertyData?.id) this.propertyState.activePropertyData = data;

        this.orderState.propertyStore.set(data.id!, data);

        return false;
      }),
      catchError(() => {
        return of(false);
      })
    );
  }

  /** Fetches the property condition report layout. */
  fetchPropertyConditionLayout(reportType: string): Observable<boolean> {
    const requestBody: PropertyViewApiRequest = {
      pageName: 'propertyCondition',
      reportType,
    };

    if (this.propertyState.propertyConditionLayout?.sectionList?.length) return of(false);

    return this.propertyApi.getActivePropertyPageView(requestBody).pipe(
      map((res) => {
        this.propertyState.propertyConditionLayout = res;

        return false;
      }),
      catchError(() => {
        this.propertyState.propertyConditionLayout = null;
        return of(false);
      })
    );
  }

  /** Fetches the details of any property in an order; stored in propertyState. */
  fetchPropertyDetail(
    reportType: string,
    propertyId: string,
    listingId?: string,
    pmxProperty?: boolean
  ): Observable<boolean> {
    return this._fetchPropertyData(reportType, propertyId, listingId, pmxProperty).pipe(
      map((data) => {
        // this.propertyState.activePropertyView = view.sectionList;
        this.propertyState.activePropertyData = data;
        this.orderState.propertyStore.set(data.id!, data);
        return false;
      }),
      startWith(true),
      catchError(() => {
        // this.propertyState.activePropertyView = [];
        this.propertyState.activePropertyData = null;

        return of(false);
      })
    );
  }

  /** Fetches asynchronous attributes of a property. */
  fetchPropertyDetailForAsyncStatus(
    reportType: string,
    propertyId: string,
    listingId?: string,
    pmxProperty?: boolean,
    responseKeys?: PropertyAttribute[]
  ): Observable<any> {
    return this._fetchPropertyData(reportType, propertyId, listingId, pmxProperty, responseKeys).pipe(
      map((data) => data),
      catchError(() => {
        return of(null);
      })
    );
  }

  /** Fetches the details of any property history in an order; stored in propertyState. */
  fetchHistoryPropertyDetail(reportType: string, propertyId: string, listingId: string): Observable<boolean> {
    return this._fetchPropertyData(reportType, propertyId, listingId).pipe(
      map((data) => {
        this.propertyState.activePropertyHistoryData = data;
        return false;
      }),
      startWith(true),
      catchError(() => {
        this.propertyState.activePropertyHistoryData = null;
        return of(false);
      })
    );
  }

  public getCurrentStatusAndPrice(property: PropertyDetail): { status: StandardStatusType; price: number | null } {
    const currentStatus: StandardStatusType = (this.selectedSourceData.transform(property.StandardStatus) ??
      property.StandardStatus) as StandardStatusType;

    let currentPrice = 0;

    if ([StandardStatusType.active, StandardStatusType.activeUC, StandardStatusType.pending].includes(currentStatus))
      currentPrice = Number(this.selectedSourceData.transform(property.ListPrice) ?? property.ListPrice);
    else if (currentStatus == StandardStatusType.sold) {
      currentPrice = Number(this.selectedSourceData.transform(property.ClosePrice) ?? property.ClosePrice);
    } else if (
      [StandardStatusType.canceled, StandardStatusType.expired, StandardStatusType.delisted].includes(currentStatus)
    )
      currentPrice = Number(
        this.selectedSourceData.transform(property.ClosePrice ?? property.LastSalePrice) ??
          property.ClosePrice ??
          property.LastSalePrice
      );

    return {
      status: currentStatus,
      price: Number.isNaN(currentPrice) ? null : currentPrice,
    };
  }

  parsePropertyRoomDetails(data: any): any {
    const resultObj: any = {};
    const objTemplate = {
      area: '',
      areaunits: '',
      areasource: '',
      dimensions: '',
      length: '',
      width: '',
      lengthwidthunits: '',
      lengthwidthsource: '',
      level: '',
      features: '',
      description: '',
    };
    Object.keys(data).forEach((key: string) => {
      const curObj = this._makeKey(key);
      curObj.uniqueKey = curObj && curObj.uniqueKey ? this._capitalize(curObj.uniqueKey) : '';
      if (curObj) {
        if (!resultObj[curObj.uniqueKey]) {
          resultObj[curObj.uniqueKey] = structuredClone(objTemplate);
        }
        let curData = data[key];
        if (this._isObject(data[key])) {
          curData = JSON.stringify(data[key]).replace(/[{}]/g, '');
        } else {
          curData = data[key]?.trim();
        }
        if (resultObj[curObj.uniqueKey][curObj.curKey]) {
          resultObj[curObj.uniqueKey][curObj.curKey] = resultObj[curObj.uniqueKey][curObj.curKey] + ' ' + curData;
        } else {
          resultObj[curObj.uniqueKey][curObj.curKey] = curData;
        }
      }
    });
    if (resultObj) {
      return Object.keys(resultObj)
        .map((roomName) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return { roomName, ...resultObj[roomName] };
        })
        .sort((a, b) => (a.roomName.toLowerCase() < b.roomName.toLowerCase() ? -1 : 1));
    } else {
      return [];
    }
  }

  /** Fetches a property from local state by id. */
  getPropertyById(propertyId: string): ExtendedPropertyDetail | null {
    if (propertyId == this.propertyState.subjectPropertyData?.id) return this.propertyState.subjectPropertyData;

    if (propertyId == this.propertyState.activePropertyData?.id) return this.propertyState.activePropertyData;

    if (this.salesApproachState.selectedComparableMap[propertyId])
      return this.salesApproachState.selectedComparableMap[propertyId];

    if (this.costApproachState.selectedComparableMap[propertyId])
      return this.costApproachState.selectedComparableMap[propertyId];

    if (this.shortTermRentalState.selectedComparableMap[propertyId])
      return this.shortTermRentalState.selectedComparableMap[propertyId];

    if (this.longTermRentalState.selectedComparableMap[propertyId])
      return this.longTermRentalState.selectedComparableMap[propertyId];

    return null;
  }

  /** Returns the subject properties across scenarios, optionally filtered by filterBy. */
  public getSubjectProperties(filterBy?: SubjectPropertyFilter): Observable<ExtendedPropertyDetail[]> {
    const allSubjectIds: string[] = [];
    const selectedSubjectIds: string[] = [];
    let requiredSubjectIds: string[] = [];

    Object.values(this.scenarioState.scenarioMap ?? {}).forEach((scenario) => {
      const subjectId = scenario.property!.id;
      allSubjectIds.push(subjectId);
      if (scenario.selected) selectedSubjectIds.push(subjectId);
    });

    const availableSubjects: ExtendedPropertyDetail[] = [];
    const unavailableSubjectIds: string[] = [];

    if (filterBy?.scenarioId) {
      const subjectId = this.scenarioState.scenarioMap?.[filterBy.scenarioId].property?.id;
      if (subjectId) requiredSubjectIds = [subjectId];
    } else if (filterBy?.isSelected) {
      requiredSubjectIds = selectedSubjectIds;
    } else if (filterBy?.isActive) {
      requiredSubjectIds = [this.scenarioState.activeScenario!.property!.id];
    } else {
      requiredSubjectIds = allSubjectIds;
    }

    requiredSubjectIds.forEach((subjectId) => {
      if (this.orderState.propertyStore.has(subjectId)) {
        availableSubjects.push(this.orderState.propertyStore.get(subjectId)!);
      } else {
        unavailableSubjectIds.push(subjectId);
      }
    });

    if (!unavailableSubjectIds.length) return of(availableSubjects);

    return forkJoin(
      unavailableSubjectIds.map((subjectId) =>
        this._fetchPropertyData(this.orderState.reportType, subjectId).pipe(
          tap((property) => this.orderState.propertyStore.set(subjectId, property))
        )
      )
    ).pipe(
      map((responseList) => {
        return responseList.concat(availableSubjects);
      })
    );
  }

  downloadPropertyCSV(data: PropertyDetail, filename: string): void {
    const csvData = this._convertJsonToCSV(data);

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    this.snackBarService.open('success', 'Property CSV downloaded successfully.');
  }

  downloadPropertyPDF(
    mlsListingNumber?: string,
    mlsBoard?: string,
    street?: string,
    zip?: string
  ): Observable<boolean> {
    const requestBody: GenerateListingSheetApiRequest = {
      OrderId: 'PROFET_VAL_PROPERTY_' + new Date().getTime(),
      ...(mlsListingNumber ? { MLSListingNumber: mlsListingNumber } : {}),
      ...(mlsBoard ? { MLSBoard: mlsBoard } : {}),
      ...(street ? { Street: street } : {}),
      ...(zip ? { Zip: zip } : {}),
    };

    return this.proxyService.initPropertyPdfGeneration(requestBody).pipe(
      switchMap((referenceId) => {
        if (!referenceId) return of(false);

        return this.proxyService.fetchGeneratedPropertyPdf(referenceId).pipe(
          repeat({ count: 5, delay: 3000 }),
          takeWhile((pdfString) => !pdfString, true),
          map((pdfString) => {
            if (pdfString) {
              const address =
                this.selectedSourceDataPipe.transform(this.propertyState.activePropertyData?.propertyStreetAddress) +
                '_' +
                this.selectedSourceDataPipe.transform(this.propertyState.activePropertyData?.propertyCity) +
                '_' +
                this.selectedSourceDataPipe.transform(this.propertyState.activePropertyData?.propertyState) +
                '_' +
                this.selectedSourceDataPipe.transform(this.propertyState.activePropertyData?.propertyZip);
              this._downloadPDF(pdfString, address);
              this.snackBarService.open('success', 'Property PDF downloaded successfully.');
            }

            return false;
          }),
          endWith(false)
        );
      }),
      startWith(true),
      catchError(({ error }: HttpErrorResponse) => {
        this.snackBarService.open('error', (error?.message as string) || 'Unable to download Property PDF.');
        return of(false);
      })
    );
  }

  /** Add a structure to the active scenario's subject property, then refresh all relevant data. */
  addStructure(scenarioId: string, structure: { name?: string; structureType?: string }): Observable<boolean> {
    return this.propertyApi
      .addStructure(scenarioId, structure)
      .pipe(switchMap((success) => (success ? this._refreshAfterStructureUnitChange() : of(false))));
  }

  /** Adds count units to a structure and applies the result to local state. */
  addUnit(
    scenarioId: string | undefined,
    structureId: number,
    propertyId: string,
    count: number,
    scope: UnitOpScope
  ): Observable<boolean> {
    return this.propertyApi.addUnit(scenarioId, structureId, propertyId, count).pipe(
      map((units) => {
        if (!units) return false;

        const formatted = this.commonService.formatPropertyData(
          units as unknown as ExtendedPropertyApiDetail[]
        ) as unknown as UnitDetail[];
        this._applyUnitsLocally(scope, propertyId, formatted);
        return true;
      })
    );
  }

  /** Delete a structure from the active scenario's subject property, then refresh all relevant data. */
  deleteStructure(scenarioId: string, structureId: number): Observable<boolean> {
    return this.propertyApi
      .deleteStructure(scenarioId, structureId)
      .pipe(switchMap((success) => (success ? this._refreshAfterStructureUnitChange() : of(false))));
  }

  /** Removes the given units from a structure and splices them out of local state. */
  removeUnits(
    scenarioId: string | undefined,
    structureId: number,
    propertyId: string,
    unitIds: number[],
    scope: UnitOpScope
  ): Observable<boolean> {
    return this.propertyApi.removeUnits(scenarioId, structureId, propertyId, unitIds).pipe(
      map((success) => {
        if (!success) return false;

        const property = this._readPropertyForScope(scope, propertyId);
        const remaining = (property?.structures?.[0]?.units ?? []).filter((unit) => !unitIds.includes(unit.id));
        this._applyUnitsLocally(scope, propertyId, remaining);
        return true;
      })
    );
  }

  /**
   * Claims every selected comparable for a copy of PropertySubType that is about to be written to the grid.
   * Call this before the copied value is written: a claimed comparable is skipped by the sub type watcher, so
   * whatever the write emits cannot start a second, interactive reconciliation of the same change.
   */
  beginSubTypeCopyToComparables(): void {
    if (!this.orderState.orderDetail?.supportsUnitAndStructure) return;

    Object.values(this.approachFactoryState.selectedComparableMap).forEach((comparable) => {
      if (comparable?.id) this._propertySubTypeCopyClaim.add(comparable.id);
    });
  }

  /**
   * Abandons an in-progress copy claim. The grid calls this if the copy cannot finish, so a comparable is never
   * left claimed - and therefore ignored by the sub type watcher - for the rest of the session.
   */
  discardSubTypeCopyClaim(): void {
    this._propertySubTypeCopyClaim.clear();
  }

  /**
   * Brings every claimed comparable's unit count in line with the subject, once the grid has written the copied
   * PropertySubType. Copying is a bulk action, so it runs without dialogs: the count comes from the subject and
   * any surplus comparable units are dropped from the end.
   */
  applySubTypeCopyToComparables(): void {
    // Released up front so no early return below can leave a comparable claimed for good.
    const claimedPropertyIds = [...this._propertySubTypeCopyClaim];
    this._propertySubTypeCopyClaim.clear();

    const scope = this.approachFactoryState.activeCompsUnitOpScope;
    if (!scope) return;

    const subject = this.propertyState.subjectPropertyData;
    if (!subject) return;

    const subjectSubType = this._readPropertySubType(subject);
    const targetUnitCount = this._resolveCopiedUnitCount(subjectSubType, subject);
    if (targetUnitCount == null) return;

    const comparableMap = this.approachFactoryState.selectedComparableMap;

    claimedPropertyIds.forEach((propertyId) => {
      const comparable = comparableMap[propertyId];
      if (!comparable) return;

      // Without a recorded value there is nothing to revert to if the unit operation fails.
      if (!this._knownPropertySubType.has(propertyId)) return;

      this._handlePropertySubTypeChange(
        scope,
        comparable,
        this._knownPropertySubType.get(propertyId),
        subjectSubType,
        targetUnitCount
      );
    });
  }

  /**
   * The unit count the comparables must match after a copy. Every sub type but Multi Family derives a fixed
   * count from config; Multi Family has none, so the subject's own units are the reference. Returns null when
   * the subject carries no units to mirror.
   */
  private _resolveCopiedUnitCount(
    subjectSubType: string | null | undefined,
    subject: ExtendedPropertyDetail
  ): number | null {
    if (!PropertyConfig.isMultifamilySubType(subjectSubType)) {
      return PropertyConfig.getRequiredUnitCount(subjectSubType);
    }

    const subjectUnitCount = subject.structures?.[0]?.units?.length ?? 0;

    return subjectUnitCount || null;
  }

  /** Watches each property map and reconciles units when PropertySubType changes. */
  private _watchPropertySubTypeChanges(): void {
    this.propertyState.subjectPropertyData$.subscribe((subject) => {
      if (subject?.id) this._reconcilePropertySubType('SUBJECT', subject);
    });

    this.salesApproachState.selectedComparableMap$.subscribe((map) => this._reconcileComparableMap('SALES_COMPS', map));
    this.costApproachState.selectedComparableMap$.subscribe((map) => this._reconcileComparableMap('COST_COMPS', map));
    this.shortTermRentalState.selectedComparableMap$.subscribe((map) => this._reconcileComparableMap('STR_COMPS', map));
    this.longTermRentalState.selectedComparableMap$.subscribe((map) => this._reconcileComparableMap('LTR_COMPS', map));

    this.propertyState.propertySubTypeEdited$.subscribe((propertyId) =>
      this._reconcileEditedPropertySubType(propertyId)
    );
  }

  /**
   * Handles the user re-picking the sub type the property already had. The value does not change, so the
   * state watcher above ignores it - only the explicit edit signal can tell it apart from any other field
   * edit. Multifamily is the one sub type where that matters: it re-opens the dialog so the unit count can
   * be changed. Every other sub type has a fixed count, so re-selecting it is genuinely a no-op.
   *
   * A real sub type change fires the watcher and this signal, but the watcher runs first (the edit paths
   * publish to state synchronously, before they emit here) and marks the property in flight, so only one
   * dialog ever opens.
   */
  private _reconcileEditedPropertySubType(propertyId: string): void {
    if (this._propertySubTypeInFlight.has(propertyId)) return;
    if (this._propertySubTypeCopyClaim.has(propertyId)) return;

    // The watcher records every property it sees. Without a recorded value there is nothing to revert to
    // if the user cancels, so leave the property to the watcher rather than risk clearing its sub type.
    if (!this._knownPropertySubType.has(propertyId)) return;

    const scope = this._resolveScopeForProperty(propertyId);
    if (!scope) return;

    const property = this._readPropertyForScope(scope, propertyId);
    if (!property) return;

    const currentValue = this._readPropertySubType(property);
    if (!PropertyConfig.isMultifamilySubType(currentValue)) return;

    this._handlePropertySubTypeChange(scope, property, this._knownPropertySubType.get(propertyId), currentValue);
  }

  /** Finds the scope holding this property: the subject first, then each approach's selected comps. */
  private _resolveScopeForProperty(propertyId: string): UnitOpScope | null {
    const scopes: UnitOpScope[] = ['SUBJECT', 'SALES_COMPS', 'COST_COMPS', 'STR_COMPS', 'LTR_COMPS'];

    return scopes.find((scope) => this._readPropertyForScope(scope, propertyId)) ?? null;
  }

  /** Stores the property's current PropertySubType as the last accepted value. */
  private _rememberSubType(property: ExtendedPropertyDetail): void {
    const id = property.id!;
    this._knownPropertySubType.set(id, this._readPropertySubType(property));
    this._knownPropertySubTypeAttr.set(id, structuredClone(property.PropertySubType ?? null));
  }

  private _reconcileComparableMap(scope: UnitOpScope, map: PropertyMap): void {
    if (!map) return;
    Object.values(map).forEach((comp) => {
      if (comp?.id) this._reconcilePropertySubType(scope, comp);
    });
  }

  private _reconcilePropertySubType(scope: UnitOpScope, property: ExtendedPropertyDetail): void {
    const id = property.id!;

    // A copy across the grid owns this comparable until it has applied its own unit change.
    if (this._propertySubTypeCopyClaim.has(id)) return;

    const currentValue = this._readPropertySubType(property);

    // While an op is in flight, ignore emissions until the property hits its settle target.
    if (this._propertySubTypeInFlight.has(id)) {
      if (this._propertySubTypeSettleTarget.has(id) && this._propertySubTypeSettleTarget.get(id) === currentValue) {
        this._propertySubTypeSettleTarget.delete(id);
        this._propertySubTypeInFlight.delete(id);
      }
      return;
    }

    // First sighting of this property: record the value without acting on it.
    if (!this._knownPropertySubType.has(id)) {
      this._rememberSubType(property);
      return;
    }

    const previousValue = this._knownPropertySubType.get(id);
    if (previousValue === currentValue) return;

    // Multifamily's unit count is entered by the user, so it is driven solely by the explicit edit signal -
    // the only trigger that also catches the user re-picking the value the property already has. Handing it
    // one owner keeps a real change from opening two dialogs. The value is deliberately not remembered here:
    // the signal handler needs the pre-change value to revert to if the user cancels.
    if (PropertyConfig.isMultifamilySubType(currentValue)) return;

    this._handlePropertySubTypeChange(scope, property, previousValue, currentValue);
  }

  /**
   * Reconciles a property's units with its new sub type. `copiedUnitCount` is set only when the change came
   * from a copy across the grid, where the count is already known and the user must not be prompted.
   */
  private _handlePropertySubTypeChange(
    scope: UnitOpScope,
    property: ExtendedPropertyDetail,
    previousValue: string | null | undefined,
    currentValue: string | null | undefined,
    copiedUnitCount?: number
  ): void {
    const id = property.id!;

    // A unit operation is already running for this property; a second one would act on units the first
    // is in the middle of adding or removing.
    if (this._propertySubTypeInFlight.has(id)) return;

    // Unit add/remove on a sub type change is a per-client feature; otherwise just a field edit.
    if (!this.orderState.orderDetail?.supportsUnitAndStructure) {
      this._rememberSubType(property);
      return;
    }

    const currentCount = property.structures?.[0]?.units?.length ?? 0;
    const structureId = property.structures?.[0]?.id;

    // No structure to act on - accept the new value as a normal field edit.
    if (structureId == null) {
      this._rememberSubType(property);
      return;
    }

    const previousAttribute = this._knownPropertySubTypeAttr.get(id) ?? null;

    // Mark in flight before resolving the count: a Multifamily count comes from a dialog, and emissions
    // arriving while it is open must not start a second reconciliation for this property.
    this._propertySubTypeInFlight.add(id);
    this._rememberSubType(property);

    const finalize = (success: boolean): void => {
      if (success) {
        this._propertySubTypeInFlight.delete(id);
        return;
      }

      this._knownPropertySubType.set(id, previousValue);
      this._knownPropertySubTypeAttr.set(id, previousAttribute);
      // Set the settle target before reverting so the revert emission releases the guard.
      this._propertySubTypeSettleTarget.set(id, previousValue);
      this._revertPropertySubType(scope, id, previousAttribute);
      this.propertyState.gridReloadRequired = { scope, propertyId: id };

      // Fallback when the revert produces no emission (e.g. the property is gone).
      if (this._propertySubTypeInFlight.has(id)) {
        Promise.resolve().then(() => {
          this._propertySubTypeInFlight.delete(id);
          this._propertySubTypeSettleTarget.delete(id);
        });
      }
    };

    this._resolveRequiredUnitCount(currentValue, currentCount, copiedUnitCount).subscribe((requiredCount) => {
      // The user dismissed the unit count dialog - revert the sub type.
      if (requiredCount == null) {
        finalize(false);
        return;
      }

      // No units to add or remove - accept the value, but still sync the derived unit count.
      if (requiredCount === currentCount) {
        this._updateDerivedUnitCount(scope, id, currentCount);
        finalize(true);
        return;
      }

      if (requiredCount > currentCount) {
        this.addUnit(undefined, structureId, id, requiredCount - currentCount, scope).subscribe(finalize);
        return;
      }

      const removalCount = currentCount - requiredCount;
      const units = property.structures?.[0]?.units ?? [];

      this._resolveUnitIdsToRemove(units, removalCount, currentValue, copiedUnitCount != null).subscribe(
        (selectedUnitIds) => {
          if (!selectedUnitIds?.length) {
            finalize(false);
            return;
          }

          this.removeUnits(undefined, structureId, id, selectedUnitIds, scope).subscribe((success) =>
            finalize(success)
          );
        }
      );
    });
  }

  /**
   * Picks which units to drop when a property ends up with more than it needs. A copy across the grid takes
   * the surplus off the end without asking; a single edit lets the user choose. Emits null when the user cancels.
   */
  private _resolveUnitIdsToRemove(
    units: UnitDetail[],
    removalCount: number,
    propertySubType: string | null | undefined,
    removeFromEnd: boolean
  ): Observable<number[] | null> {
    if (removeFromEnd) {
      return of(units.slice(units.length - removalCount).map((unit) => unit.id));
    }

    const dialogData: RemoveUnitsDialogData = {
      units: structuredClone(units),
      removalCount,
      propertySubType: propertySubType ?? '',
    };
    const dialogRef = this.dialog.open<RemoveUnitsDialogComponent, RemoveUnitsDialogData, number[]>(
      RemoveUnitsDialogComponent,
      { data: dialogData }
    );

    return dialogRef.afterClosed().pipe(map((selectedUnitIds) => selectedUnitIds ?? null));
  }

  /**
   * Resolves how many units the primary structure must end up with. Every sub type but Multifamily derives
   * a fixed count from config; Multifamily is the one the user enters. Emits null when the user cancels.
   */
  private _resolveRequiredUnitCount(
    propertySubType: string | null | undefined,
    currentUnitCount: number,
    copiedUnitCount?: number
  ): Observable<number | null> {
    // A copy across the grid already took its count from the subject, so it never prompts.
    if (copiedUnitCount != null) return of(copiedUnitCount);

    if (!PropertyConfig.isMultifamilySubType(propertySubType)) {
      return of(PropertyConfig.getRequiredUnitCount(propertySubType));
    }

    const dialogRef = this.dialog.open<UnitCountDialogComponent, UnitCountDialogData, number>(
      UnitCountDialogComponent,
      {
        width: '400px',
        data: { currentUnitCount },
      }
    );

    return dialogRef.afterClosed().pipe(map((unitCount) => unitCount ?? null));
  }

  /** Writes units to both live state and snapshot; the derived count to live state only so it still syncs. */
  private _applyUnitsLocally(scope: UnitOpScope, propertyId: string, units: UnitDetail[]): void {
    // The rollups the live copy ends up with, mirrored into the snapshot below. Recomputing the snapshot's
    // own copy could differ while it lags behind un-synced edits, and the diff would then sync a derived value.
    let liveSummary: PropertySummary | undefined;

    if (scope === 'SUBJECT') {
      const live = this.propertyState.subjectPropertyData;
      if (live?.id === propertyId) {
        const next = structuredClone(live);
        this._writeUnits(next, units);
        this._writeDerivedUnitCount(next, units.length);
        liveSummary = next.summary;
        this.propertyState.subjectPropertyData = next;
        if (this.propertyState.activePropertyData?.id === propertyId) {
          this.propertyState.activePropertyData = next;
        }
      }

      const snapshot = this.syncState.syncedSubjectPropertyData;
      if (snapshot?.id === propertyId) {
        const nextSnapshot = structuredClone(snapshot);
        this._writeUnits(nextSnapshot, units);
        if (liveSummary) nextSnapshot.summary = structuredClone(liveSummary);
        this.syncState.syncedSubjectPropertyData = nextSnapshot;
      }
    } else {
      const state = this._comparableStateForScope(scope);
      const liveMap = state.selectedComparableMap;
      if (liveMap[propertyId]) {
        const next = structuredClone(liveMap[propertyId]);
        this._writeUnits(next, units);
        this._writeDerivedUnitCount(next, units.length);
        liveSummary = next.summary;
        state.selectedComparableMap = { ...liveMap, [propertyId]: next };
      }

      const snapshotMap = this._readSyncSnapshotMap(scope);
      if (snapshotMap[propertyId]) {
        const nextSnapshot = structuredClone(snapshotMap[propertyId]);
        this._writeUnits(nextSnapshot, units);
        if (liveSummary) nextSnapshot.summary = structuredClone(liveSummary);
        this._writeSyncSnapshotMap(scope, { ...snapshotMap, [propertyId]: nextSnapshot });
      }
    }

    this.propertyState.gridReloadRequired = { scope, propertyId };
  }

  /** Sets structures[0].units on a property, keeping the derived summary rollups in step with them. */
  private _writeUnits(property: ExtendedPropertyDetail, units: UnitDetail[]): void {
    if (!property.structures?.length) return;
    property.structures[0].units = structuredClone(units);
    this.propertySummaryService.applySummary(property);
  }

  /** Sets derivedNumberOfUnitsTotal to the unit count, sourced from the user's record. */
  private _writeDerivedUnitCount(property: ExtendedPropertyDetail, count: number): void {
    property.derivedNumberOfUnitsTotal = {
      ...(property.derivedNumberOfUnitsTotal ?? {}),
      userRecord: count,
      selectedSource: 'userRecord',
    } as PropertyAttributeValue;
  }

  /** Writes the derived unit count to live state only (so it syncs), when it does not already match. */
  private _updateDerivedUnitCount(scope: UnitOpScope, propertyId: string, count: number): void {
    const nextOrNull = (property: ExtendedPropertyDetail): ExtendedPropertyDetail | null => {
      const derived = property.derivedNumberOfUnitsTotal;
      if (derived?.userRecord === count && derived?.selectedSource === 'userRecord') return null;

      const next = structuredClone(property);
      this._writeDerivedUnitCount(next, count);
      return next;
    };

    if (scope === 'SUBJECT') {
      const live = this.propertyState.subjectPropertyData;
      if (live?.id !== propertyId) return;

      const next = nextOrNull(live);
      if (!next) return;

      this.propertyState.subjectPropertyData = next;
      if (this.propertyState.activePropertyData?.id === propertyId) {
        this.propertyState.activePropertyData = next;
      }
      return;
    }

    const state = this._comparableStateForScope(scope);
    const liveMap = state.selectedComparableMap;
    if (!liveMap[propertyId]) return;

    const next = nextOrNull(liveMap[propertyId]);
    if (!next) return;

    state.selectedComparableMap = { ...liveMap, [propertyId]: next };
  }

  private _revertPropertySubType(
    scope: UnitOpScope,
    propertyId: string,
    previousAttribute: PropertyAttributeValue | null
  ): void {
    const applyRevert = (property: ExtendedPropertyDetail): ExtendedPropertyDetail => {
      const reverted = structuredClone(property);
      if (previousAttribute) {
        reverted.PropertySubType = previousAttribute;
      } else {
        delete reverted.PropertySubType;
      }
      return reverted;
    };

    if (scope === 'SUBJECT') {
      const live = this.propertyState.subjectPropertyData;
      if (live?.id !== propertyId) return;

      const reverted = applyRevert(live);
      this.propertyState.subjectPropertyData = reverted;
      if (this.propertyState.activePropertyData?.id === propertyId) {
        this.propertyState.activePropertyData = reverted;
      }
      return;
    }

    const state = this._comparableStateForScope(scope);
    const liveMap = state.selectedComparableMap;
    if (!liveMap[propertyId]) return;

    state.selectedComparableMap = { ...liveMap, [propertyId]: applyRevert(liveMap[propertyId]) };
  }

  private _readPropertyForScope(scope: UnitOpScope, propertyId: string): ExtendedPropertyDetail | null {
    if (scope === 'SUBJECT') {
      const subject = this.propertyState.subjectPropertyData;
      return subject?.id === propertyId ? subject : null;
    }

    return this._comparableStateForScope(scope).selectedComparableMap[propertyId] ?? null;
  }

  private _comparableStateForScope(
    scope: UnitOpScope
  ): SalesApproachState | CostApproachState | ShortTermRentalState | LongTermRentalState {
    switch (scope) {
      case 'COST_COMPS':
        return this.costApproachState;
      case 'STR_COMPS':
        return this.shortTermRentalState;
      case 'LTR_COMPS':
        return this.longTermRentalState;
      default:
        return this.salesApproachState;
    }
  }

  private _readSyncSnapshotMap(scope: UnitOpScope): PropertyMap {
    switch (scope) {
      case 'COST_COMPS':
        return this.syncState.syncedCostApproachSelectedComparableMap;
      case 'STR_COMPS':
        return this.syncState.syncedShortTermRentSelectedComparableMap;
      case 'LTR_COMPS':
        return this.syncState.syncedLongTermRentSelectedComparableMap;
      default:
        return this.syncState.syncedSelectedComparableMap;
    }
  }

  private _writeSyncSnapshotMap(scope: UnitOpScope, map: PropertyMap): void {
    switch (scope) {
      case 'COST_COMPS':
        this.syncState.syncedCostApproachSelectedComparableMap = map;
        break;
      case 'STR_COMPS':
        this.syncState.syncedShortTermRentSelectedComparableMap = map;
        break;
      case 'LTR_COMPS':
        this.syncState.syncedLongTermRentSelectedComparableMap = map;
        break;
      default:
        this.syncState.syncedSelectedComparableMap = map;
        break;
    }
  }

  private _readPropertySubType(property: ExtendedPropertyDetail | null | undefined): string | null | undefined {
    if (!property) return null;
    const raw = this.selectedSourceDataPipe.transform(property.PropertySubType) as unknown;
    return raw == null ? null : String(raw);
  }

  private _listenToPropertyDataChanges(): void {
    this.propertyState.activePropertyData$.subscribe((res) => {
      if (res) this.orderState.propertyStore.set(res.id!, res);
    });

    this.propertyState.subjectPropertyData$.subscribe((res) => {
      if (res) this.orderState.propertyStore.set(res.id!, res);
    });
  }

  private _refreshAfterStructureUnitChange(): Observable<boolean> {
    const reportType = this.orderState.reportType;
    const subjectId = this.scenarioState.activeScenario?.property?.id ?? '';

    const refreshSubject$ = this.fetchSubjectPropertyDetail(reportType, subjectId);

    const activeId = this.propertyState.activePropertyData?.id;
    const refreshActive$ =
      activeId && activeId !== subjectId
        ? this._fetchPropertyData(reportType, activeId).pipe(
            tap((data) => {
              this.propertyState.activePropertyData = data;
              this.orderState.propertyStore.set(data.id!, data);
            }),
            catchError(() => of(null))
          )
        : of(null);

    const activeValuationType = this.orderState.activeValuationType;
    const activeScenarioId = this.scenarioState.activeScenario?.id;
    const refreshComps$ =
      activeValuationType && activeScenarioId
        ? this.comparableService.getSelectedComparables(
            { scenarioId: activeScenarioId, valuationType: activeValuationType },
            true
          )
        : of([]);

    return forkJoin([refreshSubject$, refreshActive$, refreshComps$]).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  private _convertJsonToCSV(data: PropertyDetail): string {
    const rows: string[] = [];
    const headers = Object.keys(data).join(',');
    rows.push(headers);

    const values = Object.values(data)
      .map((value) => {
        if (value && typeof value === 'object' && 'selectedValue' in value) {
          let val = value.selectedValue ?? '';
          if (typeof val === 'string' && val.includes(',')) {
            val = `"${val.replace(/"/g, '""')}"`;
          }
          return val;
        }
        return '';
      })
      .join(',');

    rows.push(values);
    return rows.join('\n');
  }

  private _downloadPDF(pdfData: string, fileName: string): void {
    const byteNumbers = new Array(pdfData.length);
    for (let i = 0; i < pdfData.length; i++) {
      byteNumbers[i] = pdfData.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const anchorElement = document.createElement('a');
    const url = window.URL.createObjectURL(blob);
    anchorElement.href = url;
    anchorElement.download = fileName;
    anchorElement.click();
    window.URL.revokeObjectURL(url);
  }

  private _fetchPropertyData(
    reportType: string,
    propertyId: string,
    listingId?: string,
    pmxProperty?: boolean,
    responseKeys?: PropertyAttribute[]
  ): Observable<ExtendedPropertyDetail> {
    const requestBody: PropertyDataApiRequest = {
      pageName: 'propertyDetails',
      reportType,
      ...(listingId ? { listingId } : {}),
      ...(pmxProperty ? { pmxPropertyId: propertyId } : { propertyId: propertyId }),
      ...(responseKeys ? { responseKeys } : {}),
    };
    return this.propertyApi
      .getActivePropertyPageData(requestBody)
      .pipe(map((data) => this.commonService.formatPropertyData(data)));
  }

  private _isObject(value: any): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return value && typeof value === 'object' && value.constructor === Object;
  }

  private _makeKey(key: string): any {
    const roomAttributes: { [key: string]: string[] } = {
      area: ['area', 'areas'],
      areaunits: ['areaunits', 'areaunit'],
      areasource: ['areasource', 'areasources'],
      dimensions: ['dimensions', 'dimension', 'dmnsn'],
      length: ['length', 'lengths', 'lngth'],
      width: ['width', 'widths'],
      lengthwidthunits: ['lengthwidthunits', 'lengthwidthunit', 'lwu'],
      lengthwidthsource: ['lengthwidthsource', 'lengthwidthsources', 'lws'],
      level: ['level', 'lvl', 'lev'],
      features: ['features', 'feature'],
      description: ['description', 'descriptions', 'desc'],
    };
    let res: any = false;
    let tmpKey = key.toString().toLowerCase();
    if (tmpKey.startsWith('room')) {
      tmpKey = tmpKey.replace('room', '');
    }
    const postFixData = Object.keys(roomAttributes);

    for (let x = 0; x < postFixData.length; x++) {
      for (let y = 0; y < roomAttributes[postFixData[x]].length; y++) {
        const labelSearchElement = roomAttributes[postFixData[x]][y];
        tmpKey = this._isLastIndexANum(tmpKey);
        if (tmpKey.endsWith(labelSearchElement)) {
          tmpKey = tmpKey.substring(0, tmpKey.lastIndexOf(labelSearchElement));
          res = { curKey: postFixData[x], uniqueKey: tmpKey };
          break;
        }
      }
    }

    if (!res) {
      res = { curKey: 'description', uniqueKey: tmpKey };
    }
    return res;
  }

  private _isLastIndexANum(str: string): string {
    if (!isNaN(Number(str[str.length - 1]))) {
      for (let i = str.length - 1; i >= 0; i--) {
        if (this._isStringNum(str[i])) {
          str = str.substring(0, str.length - 1);
        }
      }
      return str;
    } else {
      return str;
    }
  }

  private _isStringNum(string: string): boolean {
    if (!string) return false;
    return !isNaN(Number(string));
  }

  private _capitalize(roomType: string): string {
    if (typeof roomType !== 'string') return '';
    return roomType.charAt(0).toUpperCase() + roomType.slice(1);
  }
}
